import { movies } from '@/mocks/movies';
import { genres } from '@/mocks/genres';

export interface Movie {
  id: string;
  title: string;
  year: number;
  duration: number;
  rating: number;
  region: string;
  director: string;
  actors: string[];
  genres: string[];
  genreKeys: string[];
  status: string;
  synopsis: string;
  poster: string;
  backdrop: string;
  tags: string[];
  isHot: boolean;
  isNew: boolean;
}

export interface Genre {
  key: string;
  name: string;
  name2?: string;
  icon: string;
  desc: string;
}

export const allMovies: Movie[] = movies as Movie[];
export const allGenres: Genre[] = genres as Genre[];

export const filterRegions = ["中国大陆", "美国", "英国", "韩国", "日本", "中国香港"];
export const filterYears = ["2025", "2024", "2023", "2022", "2021", "2020及以前"];
export const filterStatus = ["高清", "正片", "抢先版"];

export function getMovieById(id: string): Movie | undefined {
  return allMovies.find((movie) => movie.id === id);
}

export function getGenreByKey(key: string): Genre | undefined {
  return allGenres.find((genre) => genre.key === key);
}

export function getMoviesByGenre(key: string): Movie[] {
  return allMovies.filter((movie) => movie.genreKeys.includes(key));
}

export function getHotMovies(limit = 10): Movie[] {
  return [...allMovies]
    .filter((movie) => movie.isHot)
    .sort((a, b) => b.rating - a.rating)
    .slice(0, limit);
}

export function getNewMovies(limit = 8): Movie[] {
  return allMovies
    .filter((movie) => movie.isNew)
    .sort((a, b) => b.year - a.year)
    .slice(0, limit);
}

export function getRecommendations(movie: Movie, limit = 6): Movie[] {
  return allMovies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      const shared = item.genreKeys.filter((key) => movie.genreKeys.includes(key)).length;
      const regionBonus = item.region === movie.region ? 1 : 0;
      return { item, score: shared * 10 + regionBonus };
    })
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function searchMovies(keyword: string): Movie[] {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];
  return allMovies.filter((movie) => {
    const haystack = [
      movie.title,
      movie.director,
      movie.region,
      movie.synopsis,
      ...movie.actors,
      ...movie.genres,
      ...movie.tags,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(query);
  });
}

export type SortKey = 'hot' | 'latest' | 'rating';

export interface FilterOptions {
  region?: string;
  year?: string;
  genre?: string;
  status?: string;
  sort?: SortKey;
}

function matchYear(movie: Movie, year: string): boolean {
  if (year === '2020及以前') return movie.year <= 2020;
  return String(movie.year) === year;
}

export function applyFilters(list: Movie[], options: FilterOptions): Movie[] {
  const { region, year, genre, status, sort = 'hot' } = options;
  let result = list.filter((movie) => {
    if (region && movie.region !== region) return false;
    if (year && !matchYear(movie, year)) return false;
    if (genre && !movie.genreKeys.includes(genre)) return false;
    if (status && movie.status !== status) return false;
    return true;
  });

  result = [...result].sort((a, b) => {
    if (sort === 'latest') return b.year - a.year || b.rating - a.rating;
    if (sort === 'rating') return b.rating - a.rating;
    return b.rating - a.rating || b.year - a.year;
  });

  return result;
}

export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours <= 0) return `${mins}分钟`;
  return `${hours}小时${mins > 0 ? `${mins}分钟` : ''}`;
}